/**
 * Soniox real-time dictation display helpers.
 *
 * @see https://soniox.com/docs/stt/rt/real-time-transcription
 * @see https://github.com/soniox/soniox_examples/blob/master/speech_to_text/python_sdk/soniox_sdk_realtime.py
 *
 * - Final tokens: accumulate (server sends `transcriptCumulative`)
 * - Non-final tokens: replace each event (shown as interim preview)
 * - Render: concatenate token strings only — never add, trim, or normalize spaces
 *   (one exception: spaces right after a spoken line break, see below)
 */

export function hasSttText(value) {
  if (value == null || value === "") return false;
  return /\S/.test(String(value));
}

/** Like hasSttText, but a line break (spoken "new line", CRE) also counts. */
export function hasSttContent(value) {
  if (value == null || value === "") return false;
  return /\S|\n/.test(String(value));
}

/**
 * CRE: the server turns spoken "new line" into "\n" per chunk, so the next
 * chunk's leading space would start the new line. Drop spaces/tabs right after
 * a "\n" inside STT text only. Text without "\n" is returned unchanged, so
 * non-CRE / no-new-line dictation is byte-for-byte identical to before.
 * @param {string} sttText - text produced by STT (never the user's own text)
 * @param {boolean} [afterSttLineBreak] - previous STT chunk ended with "\n"
 */
export function trimSpacesAfterLineBreaks(sttText, afterSttLineBreak = false) {
  let out = String(sttText ?? "");
  if (out.includes("\n")) out = out.replace(/\n[ \t]+/g, "\n");
  if (afterSttLineBreak) out = out.replace(/^[ \t]+/, "");
  return out;
}

/** Soniox cumulative sync (he/fr/ar). English/es append deltas into the sidebar. */
export function getSidebarDictationSttMode(dictationLanguage) {
  const code = String(dictationLanguage ?? "").split("-")[0].toLowerCase();
  if (code === "he" || code === "fr" || code === "ar") return "soniox";
  return "incremental";
}

/** Same as Soniox `render_tokens(final_tokens, non_final_tokens)`. */
export function renderSonioxText(preRecordingText, finalsCumulative, interimText = "") {
  // Only the STT part is touched (and only around STT line breaks).
  const stt = String(finalsCumulative ?? "") + String(interimText ?? "");
  return String(preRecordingText ?? "") + trimSpacesAfterLineBreaks(stt);
}

export function isCaretAtEnd(textarea) {
  if (!textarea) return true;
  const len = textarea.value.length;
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? start;
  return start === end && end === len;
}

/** Free dictation: insert socket delta at caret without adding spaces. */
export function insertRawAtCaret(
  text,
  rawInsert,
  selectionStart,
  selectionEnd,
  { afterSttLineBreak = false } = {},
) {
  const current = text ?? "";
  if (!hasSttContent(rawInsert)) {
    return { text: current, caret: selectionStart ?? 0 };
  }
  const start = Math.max(0, Math.min(selectionStart ?? 0, current.length));
  const end = Math.max(start, Math.min(selectionEnd ?? start, current.length));
  const chunk = trimSpacesAfterLineBreaks(
    rawInsert,
    afterSttLineBreak && current.slice(0, start).endsWith("\n"),
  );
  const next = current.slice(0, start) + chunk + current.slice(end);
  return { text: next, caret: start + chunk.length };
}
